import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faLink } from '@fortawesome/free-solid-svg-icons';
import './ReviewFieldOrderList.scss';

/**
 * Put every linked slot that sits above its parent just after that parent,
 * the same rule the drop applies, so mid-drag numbers show where cards land.
 */
const keepLinkedBelowParent = (order, linkedTo) => Object.entries(linkedTo).reduce((current, [slot, parent]) => {
    const parentIndex = current.indexOf(parent);
    const slotIndex = current.indexOf(slot);
    if (parentIndex === -1 || slotIndex === -1 || slotIndex > parentIndex) {
        return current;
    }
    const withoutSlot = current.filter((item) => item !== slot);
    const insertAt = withoutSlot.indexOf(parent) + 1;
    return [...withoutSlot.slice(0, insertAt), slot, ...withoutSlot.slice(insertAt)];
}, order);

/** The order the cards will have once dropped, including mid-drag. */
const previewOrder = (slots, dragPreview, linkedTo) => {
    if (!dragPreview) {
        return slots;
    }
    const next = [...slots];
    const [moved] = next.splice(dragPreview.from, 1);
    next.splice(dragPreview.to, 0, moved);
    return keepLinkedBelowParent(next, linkedTo);
};

/**
 * Draggable list of the expense review form's main fields (XPEN-3652).
 *
 * Each field is a card that looks movable: a grip handle, its position number,
 * a lift on hover and a tilt while dragged. Position numbers follow the order
 * live during a drag and show where each card will land, so a linked card held
 * above its parent is already numbered just after it. A card linked to another one (Sub-category to Category)
 * has its own look, and when it sits right below its parent in the saved order
 * the two cards join: no gap, one shared border.
 *
 * Layout rule for react-beautiful-dnd: the spacing between cards lives on the
 * draggable element itself (padding, never a child margin that collapses out
 * of it), and no card changes size during a drag, so the library's
 * measurements stay exact.
 *
 * Cards are dragged by their grip handle only, never by the card body, so a
 * click on a toggle inside a card can never start a drag. Keyboard dragging
 * (Space to lift, arrows to move, Space to drop, Escape to cancel) comes from
 * react-beautiful-dnd, which also announces positions to screen readers.
 *
 * The list knows nothing about the stored order: it reports each drop as
 * positions in `slots` (the drawn list) and the caller maps them back.
 *
 * @param {object} props
 * @param {string[]} props.slots the slots to draw, in order
 * @param {(slot: string) => React.ReactNode} props.renderRow draws a card's body
 * @param {(slot: string) => string} props.getHandleLabel accessible name of a card's handle
 * @param {(fromIndex: number, toIndex: number) => void} props.onMove called once per real move
 * @param {Object<string, string>} [props.linkedTo] linked slot → the slot it belongs to
 * @param {string} [props.linkLabel] accessible name of the link icon on a linked card
 */
const ReviewFieldOrderList = ({ slots, renderRow, getHandleLabel, onMove, linkedTo = {}, linkLabel }) => {
    // Drag-local display state only: which position the dragged card is
    // hovering over, so the numbers can follow it before the drop.
    const [dragPreview, setDragPreview] = useState(null);
    const displayed = previewOrder(slots, dragPreview, linkedTo);

    const handleDragUpdate = ({ source, destination }) => {
        setDragPreview({ from: source.index, to: destination ? destination.index : source.index });
    };

    const handleDragEnd = ({ source, destination }) => {
        setDragPreview(null);
        if (!destination || destination.index === source.index) {
            return;
        }
        onMove(source.index, destination.index);
    };

    return (
        <DragDropContext onDragUpdate={handleDragUpdate} onDragEnd={handleDragEnd}>
            <Droppable droppableId="expense-field-order">
                {(droppable) => (
                    <div
                        className="review-field-order-list"
                        ref={droppable.innerRef}
                        {...droppable.droppableProps}
                    >
                        {slots.map((slot, index) => {
                            const displayedIndex = displayed.indexOf(slot);
                            const parent = linkedTo[slot];
                            // From the saved order, not the mid-drag preview, so
                            // nothing changes shape while a card is moving.
                            const isJoined = Boolean(parent) && slots[index - 1] === parent;
                            const isJoinedParent = linkedTo[slots[index + 1]] === slot;
                            return (
                                <Draggable key={slot} draggableId={slot} index={index}>
                                    {(draggable, snapshot) => (
                                        <div
                                            className={`review-field-order-item${isJoinedParent ? ' is-joined-parent' : ''}`}
                                            ref={draggable.innerRef}
                                            {...draggable.draggableProps}
                                        >
                                            <div
                                                className={[
                                                    'review-field-order-card',
                                                    snapshot.isDragging ? 'is-dragging' : '',
                                                    parent ? 'is-linked' : '',
                                                    isJoined ? 'is-joined' : '',
                                                    isJoinedParent ? 'is-joined-parent' : '',
                                                ].filter(Boolean).join(' ')}
                                                data-testid={`review-field-card-${slot}`}
                                                data-linked={parent ? 'true' : undefined}
                                                data-joined={isJoined ? 'true' : undefined}
                                                data-joined-parent={isJoinedParent ? 'true' : undefined}
                                            >
                                                <span
                                                    className="review-field-order-handle"
                                                    {...draggable.dragHandleProps}
                                                    aria-label={getHandleLabel(slot)}
                                                >
                                                    <FontAwesomeIcon icon={faGripVertical} />
                                                </span>
                                                <span
                                                    className="review-field-order-position"
                                                    data-testid={`review-field-position-${slot}`}
                                                    aria-hidden="true"
                                                >
                                                    {displayedIndex + 1}
                                                </span>
                                                <div className="review-field-order-body">{renderRow(slot)}</div>
                                                {parent && (
                                                    <FontAwesomeIcon
                                                        className="review-field-order-link"
                                                        icon={faLink}
                                                        title={linkLabel}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            );
                        })}
                        {droppable.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default ReviewFieldOrderList;
